
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineVideoCamera,
  HiOutlinePlay,
  HiOutlineArrowDown,
  HiOutlineArrowUpRight,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineRectangleStack,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import { Loader, VideoCard } from "../components/utils";
import { getAllVideos } from "../redux/actions/videos";
import VideoThumbnail from "../components/videos/VideoThumbnail";

const PAGE_SIZE = 9;

function getVisiblePages(page, total) {
  const count = Math.min(total, 5);
  const start = Math.max(
    0,
    Math.min(page - 2, total - count)
  );

  return Array.from(
    { length: count },
    (_, index) => start + index
  );
}

function getPlainText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function VideosPage() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const category = location.state?.category || null;
  const categoryId = category?._id;

  const [page, setPage] = useState(0);

  const {
    isLoading,
    Allvideos,
    pagination,
  } = useSelector((state) => state.Videos || {});

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  useEffect(() => {
    setPage(0);
  }, [categoryId]);

  useEffect(() => {
    if (!categoryId) return;

    dispatch(getAllVideos(page, PAGE_SIZE, categoryId));
  }, [dispatch, page, categoryId]);

  const videos = Array.isArray(Allvideos)
    ? Allvideos
    : [];

  const totalPages = Math.max(
    1,
    Number(pagination?.totalPages) || 1
  );

  const categoryTitle = category
    ? isEnglish
      ? category.titleEn || category.titleAr
      : category.titleAr || category.titleEn
    : "";

  const categoryDescription = getPlainText(
    isEnglish
      ? category?.textEn || category?.textAr
      : category?.textAr || category?.textEn
  );

  const content = {
    eyebrow: isEnglish
      ? "AL NOMAN VIDEO LIBRARY"
      : "المكتبة المرئية لمؤسسة النعمان",

    categories: isEnglish
      ? "Video Categories"
      : "تصنيفات الفيديوهات",

    explore: isEnglish
      ? "Watch the Videos"
      : "استعرض الفيديوهات",

    collection: isEnglish
      ? "LEGAL VIDEO COLLECTION"
      : "مجموعة فيديوهات قانونية",

    fallbackDescription: isEnglish
      ? "Explore legal videos and explanations related to this subject."
      : "شاهد مجموعة من الفيديوهات والشروحات القانونية المرتبطة بهذا الموضوع.",

    sectionEyebrow: isEnglish
      ? "EXPLORE THE COLLECTION"
      : "استكشف المجموعة",

    sectionTitle: isEnglish
      ? "Videos in This Category"
      : "فيديوهات هذا التصنيف",

    sectionDescription: isEnglish
      ? "Browse the available videos and select a topic to watch its full explanation."
      : "تصفح الفيديوهات المتاحة واختر الموضوع الذي يهمك لمشاهدة الشرح الكامل.",

    available: isEnglish
      ? "Available Videos"
      : "الفيديوهات المتاحة",

    emptyTitle: isEnglish
      ? "No Videos Available"
      : "لا توجد فيديوهات متاحة",

    emptyText: isEnglish
      ? "Videos in this category will appear here when available."
      : "ستظهر الفيديوهات الخاصة بهذا التصنيف هنا عند إضافتها.",

    missingTitle: isEnglish
      ? "Video Category Not Found"
      : "تعذر تحديد تصنيف الفيديوهات",

    missingText: isEnglish
      ? "Please return to the video categories page and choose a category."
      : "يرجى العودة إلى صفحة تصنيفات الفيديوهات واختيار التصنيف الذي ترغب في استعراضه.",

    previous: isEnglish ? "Previous" : "السابق",
    next: isEnglish ? "Next" : "التالي",
    page: isEnglish ? "Page" : "صفحة",
    of: isEnglish ? "of" : "من",
  };

  const scrollToVideos = () => {
    document
      .getElementById("category-videos")
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  };

  const changePage = (next) => {
    if (
      next < 0 ||
      next >= totalPages ||
      next === page ||
      isLoading
    ) {
      return;
    }

    setPage(next);
    scrollToVideos();
  };

  // Direct navigation without React Router state
  if (!categoryId) {
    return (
      <>
        <Header active={5} />

        <main className="flex min-h-[75vh] items-center justify-center bg-[#F0F5F8] px-5 py-28 dark:bg-[#0D2030]">
          <div className="max-w-[550px] text-center">

            <div className="mx-auto mb-7 flex h-20 w-20 items-center justify-center rounded-2xl border border-[#D9E4EC] bg-white text-[#C19B60] dark:border-white/10 dark:bg-[#1B3549]">
              <HiOutlineVideoCamera size={37} />
            </div>

            <h1 className="h3 text-[#19344D] dark:text-white">
              {content.missingTitle}
            </h1>

            <p className="body-2 mt-5 text-[#62778A] dark:text-[#B5C8D6]">
              {content.missingText}
            </p>

            <Link
              to="/VideoCategoryPage"
              className="mt-8 inline-flex min-h-[52px] items-center gap-3 rounded-xl bg-[#19344D] px-6 py-3 font-kufi text-sm font-semibold text-white transition-colors hover:bg-[#294961]"
            >
              {content.categories}

              <HiOutlineArrowUpRight
                size={19}
                className="rtl:-scale-x-100"
              />
            </Link>
          </div>
        </main>

        <Fotter />
      </>
    );
  }

  const visiblePages = getVisiblePages(page, totalPages);

  return (
    <>
      <Header active={5} />

      <main className="overflow-hidden bg-[#F0F5F8] dark:bg-[#0D2030]">

        {/* ======================================
            01 - CATEGORY HERO
        ====================================== */}

        <section
          className="
            relative isolate overflow-hidden
            bg-[#102638] text-white
            dark:bg-[#081927]
          "
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -end-40 -top-40 h-[450px] w-[450px] rounded-full bg-[#668DA5]/10 blur-[100px]"
          />

          <div className="container relative">

            <div className="grid items-center gap-10 py-14 md:py-16 lg:min-h-[470px] lg:grid-cols-2 lg:gap-16">

              {/* Text */}
              <div className="min-w-0">

                <div className="mb-8 flex flex-wrap items-center gap-3 font-kufi text-xs text-white/60">
                  <Link
                    to="/VideoCategoryPage"
                    className="transition-colors hover:text-white"
                  >
                    {content.categories}
                  </Link>

                  <span className="h-px w-6 bg-[#D7B77F]" />

                  <span className="text-[#D7B77F]">
                    {categoryTitle}
                  </span>
                </div>

                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-9 bg-[#D7B77F]" />

                  <span className="eyebrow text-[#D7B77F]">
                    {content.eyebrow}
                  </span>
                </div>

                <h1 className="h1 max-w-[680px] text-white">
                  {categoryTitle}
                </h1>

                <p className="body-1 mt-6 max-w-[620px] text-[#C3D2DD]">
                  {categoryDescription || content.fallbackDescription}
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-4">

                  <button
                    type="button"
                    onClick={scrollToVideos}
                    className="
                      group inline-flex min-h-[52px]
                      items-center gap-3 rounded-xl
                      border border-white/20 bg-white/10
                      px-6 py-3 font-kufi text-sm font-semibold
                      text-white backdrop-blur-xl
                      transition-all duration-300
                      hover:border-white/40 hover:bg-white/20
                      focus-visible:outline-none
                      focus-visible:ring-2
                      focus-visible:ring-[#D7B77F]
                    "
                  >
                    <HiOutlinePlay
                      size={21}
                      aria-hidden="true"
                    />

                    {content.explore}

                    <HiOutlineArrowDown
                      size={18}
                      aria-hidden="true"
                      className="transition-transform group-hover:translate-y-1"
                    />
                  </button>

                </div>

                <div className="mt-10 flex items-center gap-3 border-t border-white/10 pt-5">
                  <HiOutlineVideoCamera
                    size={22}
                    className="text-[#D7B77F]"
                    aria-hidden="true"
                  />

                  <span className="body-3 text-white/65">
                    {content.collection}
                  </span>
                </div>
              </div>

              {/* Image / Video showcase */}
              <div className="relative min-w-0">

                <div
                  aria-hidden="true"
                  className="absolute -end-4 -top-4 h-28 w-28 border-e border-t border-[#D7B77F]/60"
                />

                <div className="relative overflow-hidden rounded-[20px] border border-white/15 bg-[#19344D] shadow-[0_25px_65px_rgba(0,0,0,0.20)]">

                  <VideoThumbnail
                    image={category.Image}
                    alt={categoryTitle}
                    priority
                    className="aspect-[16/10] w-full lg:aspect-[16/11]"
                  />

                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#061522]/70 via-transparent to-transparent"
                  />

                  <div
                    aria-hidden="true"
                    className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/35 bg-white/15 text-white backdrop-blur-lg"
                  >
                    <HiOutlinePlay size={34} />
                  </div>

                  <div className="absolute bottom-5 start-5 flex items-center gap-3 rounded-lg border border-white/15 bg-[#102031]/60 px-4 py-3 text-white backdrop-blur-lg">
                    <HiOutlineVideoCamera
                      size={19}
                      className="text-[#D7B77F]"
                    />

                    <span className="font-kufi text-sm font-medium">
                      {content.collection}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 - VIDEO LIBRARY
        ====================================== */}

        <section
          id="category-videos"
          aria-labelledby="category-videos-heading"
          className="
            scroll-mt-[90px]
            border-t border-[#D9E4EC]
            bg-[#F0F5F8]
            py-16 md:py-20 lg:py-24

            dark:border-white/10
            dark:bg-[#10283A]
          "
        >
          <div className="container">

            {/* Section heading */}
            <div className="mb-12 flex flex-col justify-between gap-7 lg:flex-row lg:items-end">

              <div className="max-w-[730px]">

                <div className="mb-5 flex items-center gap-3">
                  <span className="h-px w-9 bg-[#C19B60]" />

                  <span className="eyebrow text-[#A47D41] dark:text-[#D7B77F]">
                    {content.sectionEyebrow}
                  </span>
                </div>

                <h2
                  id="category-videos-heading"
                  className="h2 text-[#19344D] dark:text-white"
                >
                  {content.sectionTitle}
                </h2>

                <p className="body-2 mt-5 text-[#62778A] dark:text-[#B5C8D6]">
                  {content.sectionDescription}
                </p>
              </div>

              <div className="hidden items-center gap-3 lg:flex">
                <span className="font-kufi text-sm text-[#62778A] dark:text-[#B5C8D6]">
                  {content.available}
                </span>

                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-[#D9E4EC] bg-white text-[#C19B60] dark:border-white/10 dark:bg-[#1B3549]">
                  <HiOutlineRectangleStack size={24} />
                </div>
              </div>
            </div>

            {/* Videos */}
            {isLoading ? (
              <div className="flex min-h-[340px] items-center justify-center">
                <Loader />
              </div>
            ) : videos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
                  {videos.map((video, index) => (
                    <VideoCard
                      key={video._id || index}
                      video={video}
                      category={category}
                      index={page * PAGE_SIZE + index}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav
                    aria-label={
                      isEnglish
                        ? "Videos pagination"
                        : "ترقيم صفحات الفيديوهات"
                    }
                    className="mt-14 flex flex-col items-center justify-between gap-6 border-t border-[#D9E4EC] pt-8 sm:flex-row dark:border-white/10"
                  >
                    <div className="flex items-center gap-2 font-kufi text-sm text-[#62778A] dark:text-[#B5C8D6]">
                      {content.page}

                      <span className="font-bold text-[#19344D] dark:text-white">
                        {page + 1}
                      </span>

                      {content.of}

                      <span className="tabular-nums">
                        {totalPages}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-2">

                      <button
                        type="button"
                        onClick={() => changePage(page - 1)}
                        disabled={page === 0 || isLoading}
                        aria-label={content.previous}
                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D9E4EC] bg-white text-[#19344D] transition-colors hover:border-[#C19B60] disabled:cursor-not-allowed disabled:opacity-35 dark:border-white/10 dark:bg-[#1B3549] dark:text-white"
                      >
                        <HiOutlineChevronLeft
                          size={20}
                          className="rtl:-scale-x-100"
                        />
                      </button>

                      {visiblePages.map((number) => (
                        <button
                          key={number}
                          type="button"
                          onClick={() => changePage(number)}
                          aria-current={
                            number === page ? "page" : undefined
                          }
                          aria-label={`${content.page} ${number + 1}`}
                          className={`
                            flex h-11 min-w-[44px] items-center
                            justify-center rounded-xl border px-3
                            font-kufi text-sm font-semibold
                            transition-all duration-200

                            ${
                              number === page
                                ? "border-[#19344D] bg-[#19344D] text-white dark:border-white/20 dark:bg-white/15"
                                : "border-[#D9E4EC] bg-white text-[#62778A] hover:border-[#C19B60] dark:border-white/10 dark:bg-[#1B3549] dark:text-white"
                            }
                          `}
                        >
                          {String(number + 1).padStart(2, "0")}
                        </button>
                      ))}

                      <button
                        type="button"
                        onClick={() => changePage(page + 1)}
                        disabled={
                          page >= totalPages - 1 || isLoading
                        }
                        aria-label={content.next}
                        className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#D9E4EC] bg-white text-[#19344D] transition-colors hover:border-[#C19B60] disabled:cursor-not-allowed disabled:opacity-35 dark:border-white/10 dark:bg-[#1B3549] dark:text-white"
                      >
                        <HiOutlineChevronRight
                          size={20}
                          className="rtl:-scale-x-100"
                        />
                      </button>

                    </div>
                  </nav>
                )}
              </>
            ) : (
              <div className="flex min-h-[310px] flex-col items-center justify-center rounded-2xl border border-[#D9E4EC] bg-white px-6 py-12 text-center dark:border-white/10 dark:bg-[#1B3549]">

                <HiOutlineVideoCamera
                  size={42}
                  className="mb-5 text-[#C19B60]"
                />

                <h3 className="h5 text-[#19344D] dark:text-white">
                  {content.emptyTitle}
                </h3>

                <p className="body-2 mt-4 max-w-[460px] text-[#62778A] dark:text-[#B5C8D6]">
                  {content.emptyText}
                </p>
              </div>
            )}

          </div>
        </section>
      </main>

      <Fotter />
    </>
  );
}

export default VideosPage;
